import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * useState sincronizado con localStorage. Al montar lee el valor persistido
 * (si existe y parsea) y cada cambio lo guarda. Permite un `validate` para
 * descartar valores obsoletos (p.ej. paginas a las que ya no se tiene permiso).
 *
 * Uso:
 *   const [currentPage, setCurrentPage] = usePersistentState('agorax_worker_page', 'chats');
 *
 *   // con validacion:
 *   const [activeTab, setActiveTab] = usePersistentState('agorax_worker_tab', 'pedidos', {
 *     validate: (v) => panelesVisibles.some((p) => p.id === v),
 *   });
 *
 * - Al limpiar el valor se elimina la clave (para borradores de formularios).
 * - `clear()` devuelve al estado inicial y borra la clave.
 */
export default function usePersistentState(key, initialValue, options = {}) {
  const { validate, serializer = JSON.stringify, deserializer = JSON.parse } = options;
  const initialValueRef = useRef(initialValue);

  const [state, setState] = useState(() => {
    try {
      const raw = localStorage.getItem(key);
      if (raw === null) return initialValue;
      const parsed = deserializer(raw);
      if (typeof validate === 'function' && !validate(parsed)) return initialValue;
      return parsed;
    } catch (e) {
      return initialValue;
    }
  });

  useEffect(() => {
    try {
      if (state === undefined || state === null) {
        localStorage.removeItem(key);
      } else {
        localStorage.setItem(key, serializer(state));
      }
    } catch (e) {
      /* storage lleno o no disponible: ignorar */
    }
  }, [key, state, serializer]);

  const clear = useCallback(() => {
    try {
      localStorage.removeItem(key);
    } catch (e) {
      /* noop */
    }
    setState(initialValueRef.current);
  }, [key]);

  return [state, setState, clear];
}
